// client/src/components/pages/AmendmentsAllocateTransactions/SourceUpdatesTab.tsx
//
// ST-00212: the Amendments page's "Source updates" tab. Corrections the source overtook (the
// source changed a corrected field after the correction, so the source value is used and the
// correction was retired into the ticket's history), corrections the source now agrees with,
// and corrections re-attached to a ticket the upstream re-issued under a new uid.
// GET /api/amendments/v3/source-updates; Acknowledge needs amendments.edit.
import { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaSpinner } from 'react-icons/fa';
import {
  acknowledgeSourceUpdate,
  followSourceErrorText,
  getSourceUpdates,
} from '../../../services/amendmentFollowSource';
import type { SourceUpdateItem, SourceUpdatesResponse } from '../../../types/amendmentFollowSource';
import { formatAppDateTime, formatMiningDateTime } from '../../../utils/dateUtils';
import {
  SOURCE_UPDATE_KIND,
  formatFieldValue,
  routeText,
  sourceUpdateKind,
} from './followSourceFormat';
import {
  BUTTON_SECONDARY,
  Banner,
  EmptyRow,
  ListPager,
  TABLE,
  TBODY,
  TD,
  TH,
  THEAD,
} from './FollowSourceParts';

const PAGE_SIZE = 100;
const COLUMNS = 11;

export interface SourceUpdatesTabProps {
  /** amendments.edit: the Acknowledge buttons. */
  canEdit: boolean;
  /** The unacknowledged count of the default list (overtaken + re-attached), for the tab label. */
  onUnacknowledgedChange?: (count: number) => void;
}

export function SourceUpdateBadge({
  item,
}: {
  item: Pick<SourceUpdateItem, 'eventType' | 'adopted'>;
}) {
  const kind = SOURCE_UPDATE_KIND[sourceUpdateKind(item)];
  return (
    <span
      title={kind.title}
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${kind.className}`}
    >
      {kind.label}
    </span>
  );
}

const SourceUpdatesTab = ({ canEdit, onUnacknowledgedChange }: SourceUpdatesTabProps) => {
  const [includeAcknowledged, setIncludeAcknowledged] = useState(false);
  const [includeAdopted, setIncludeAdopted] = useState(false);
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState<SourceUpdatesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getSourceUpdates(
      { acknowledged: includeAcknowledged, adopted: includeAdopted, limit: PAGE_SIZE, offset },
      { signal: controller.signal }
    )
      .then((body) => {
        if (controller.signal.aborted) return;
        setData(body);
        // The tab label counts the default list (without the rows the source agrees with).
        if (!includeAdopted) onUnacknowledgedChange?.(body.unacknowledged);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(followSourceErrorText(err, 'Could not load the source updates.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // onUnacknowledgedChange is a callback prop: re-running on its identity would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeAcknowledged, includeAdopted, offset, reloadKey]);

  const acknowledge = useCallback(async (item: SourceUpdateItem) => {
    setBusyId(item.eventId);
    setActionError(null);
    try {
      await acknowledgeSourceUpdate(item.eventId);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setActionError(followSourceErrorText(err, 'Could not acknowledge the source update.'));
    } finally {
      setBusyId(null);
    }
  }, []);

  const items = data?.items ?? [];

  return (
    <div>
      <p className="px-4 py-2 text-xs text-slate-500 border-b border-slate-200">
        When the source changes a field you corrected, the source&apos;s new value is used and your
        correction is kept in the ticket&apos;s history. Fields you never corrected always follow
        the source. When the upstream system re-issues a ticket, your corrections move to the new
        ticket.
      </p>

      <div className="flex flex-wrap items-center gap-4 px-4 py-2 border-b border-slate-200 bg-slate-50/50 text-xs text-slate-600">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            checked={includeAcknowledged}
            onChange={(e) => {
              setIncludeAcknowledged(e.target.checked);
              setOffset(0);
            }}
          />
          Show acknowledged
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            checked={includeAdopted}
            onChange={(e) => {
              setIncludeAdopted(e.target.checked);
              setOffset(0);
            }}
          />
          Show where the source agrees with the correction
        </label>
        {data && (
          <span className="ml-auto tabular-nums text-slate-500">
            {data.unacknowledged.toLocaleString('en-US')} not acknowledged
          </span>
        )}
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {actionError && (
        <Banner tone="error" onDismiss={() => setActionError(null)}>
          {actionError}
        </Banner>
      )}

      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>Ticket No</th>
              <th className={TH}>Weigh time</th>
              <th className={TH}>Type</th>
              <th className={TH}>Route</th>
              <th className={TH}>Field</th>
              <th className={TH}>Your value</th>
              <th className={TH}>Value when corrected</th>
              <th className={TH}>New source value</th>
              <th className={TH}>When</th>
              <th className={TH}>Event</th>
              <th className={TH}>
                <span className="sr-only">Acknowledged</span>
              </th>
            </tr>
          </thead>
          <tbody className={TBODY}>
            {loading && !data ? (
              <EmptyRow colSpan={COLUMNS}>
                <FaSpinner className="inline w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />
                Loading source updates…
              </EmptyRow>
            ) : items.length === 0 && !error ? (
              <EmptyRow colSpan={COLUMNS}>
                {includeAcknowledged
                  ? 'No source updates.'
                  : 'Nothing new: the source has not changed any field you corrected.'}
              </EmptyRow>
            ) : (
              items.map((item) => {
                const isMove = item.eventType !== 'correction_retired';
                return (
                  <tr key={item.eventId} data-testid={`source-update-${item.eventId}`}>
                    <td className={`${TD} font-mono whitespace-nowrap`}>{item.ticketNo || '—'}</td>
                    <td className={`${TD} whitespace-nowrap tabular-nums`}>
                      {formatMiningDateTime(item.ticketTimestamp) || '—'}
                    </td>
                    <td className={TD}>{item.ticketType || '—'}</td>
                    <td className={TD}>{routeText(item.dispatchLocation, item.receiptLocation)}</td>
                    {isMove ? (
                      <td className={`${TD} text-slate-500`} colSpan={4}>
                        The upstream system re-issued this ticket. Your correction moved to it.
                      </td>
                    ) : (
                      <>
                        <td className={`${TD} font-medium`}>{item.field || '—'}</td>
                        <td className={TD}>
                          {/* An overtaken correction no longer counts: struck through. */}
                          <span className={item.adopted ? '' : 'line-through text-slate-400'}>
                            {formatFieldValue(item.yourValue)}
                          </span>
                        </td>
                        <td className={TD}>{formatFieldValue(item.valueWhenCorrected)}</td>
                        <td className={`${TD} font-medium text-slate-900`}>
                          {formatFieldValue(item.newSourceValue)}
                        </td>
                      </>
                    )}
                    <td className={`${TD} whitespace-nowrap tabular-nums`}>
                      {formatAppDateTime(item.createdAt)}
                    </td>
                    <td className={TD}>
                      <SourceUpdateBadge item={item} />
                    </td>
                    <td className={`${TD} whitespace-nowrap`}>
                      {item.acknowledgedAt ? (
                        <span className="text-slate-500">
                          Seen by {item.acknowledgedBy || '—'}
                          <br />
                          <span className="tabular-nums">
                            {formatAppDateTime(item.acknowledgedAt)}
                          </span>
                        </span>
                      ) : canEdit ? (
                        <button
                          type="button"
                          className={BUTTON_SECONDARY}
                          disabled={busyId !== null}
                          onClick={() => acknowledge(item)}
                          aria-label={`Acknowledge ${item.ticketNo || 'source update'}${item.field ? ` ${item.field}` : ''}`}
                        >
                          {busyId === item.eventId ? (
                            <FaSpinner className="w-3 h-3 animate-spin" aria-hidden="true" />
                          ) : (
                            <FaCheck className="w-3 h-3" aria-hidden="true" />
                          )}
                          Acknowledge
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <ListPager
          offset={offset}
          pageSize={PAGE_SIZE}
          shown={items.length}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
};

export default SourceUpdatesTab;
